import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { stepsForDelivered } from '@/lib/deliverables'
import { supabase, describeError, type Row } from '@/lib/supabase'

export type DeliverableStep = Row<'deliverable_steps'>
export type Deliverable = Row<'job_deliverables'> & { steps: DeliverableStep[] }

export const deliverableKeys = {
  forJob: (jobId: string) => ['job-deliverables', jobId] as const,
  editing: ['editing-queue'] as const,
}

export function useJobDeliverables(jobId: string) {
  return useQuery({
    queryKey: deliverableKeys.forJob(jobId),
    queryFn: async (): Promise<Deliverable[]> => {
      const { data, error } = await supabase
        .from('job_deliverables')
        .select('*, steps:deliverable_steps(*)')
        .eq('job_id', jobId)
        .order('sort_order')
        .order('created_at')
      if (error) throw error
      return data.map((item) => ({ ...item, steps: [...item.steps].sort((a, b) => a.sort_order - b.sort_order) }))
    },
  })
}

function useInvalidate(jobId: string) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: deliverableKeys.forJob(jobId) })
    void queryClient.invalidateQueries({ queryKey: deliverableKeys.editing })
    // A tick can move the wedding to another column (jobs_auto_stage).
    void queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    void queryClient.invalidateQueries({ queryKey: ['pipeline-board'] })
  }
}

export function useAddDeliverables(jobId: string) {
  const invalidate = useInvalidate(jobId)
  return useMutation({
    mutationFn: async (items: Array<{ title: string; kind: Deliverable['kind']; sort_order: number }>) => {
      if (items.length === 0) return
      const { error } = await supabase.from('job_deliverables').insert(items.map((item) => ({ ...item, job_id: jobId })))
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not add that', { description: describeError(error) }),
  })
}

export function useToggleDeliverable(jobId: string) {
  const invalidate = useInvalidate(jobId)
  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await supabase
        .from('job_deliverables')
        .update({ done_at: done ? new Date().toISOString() : null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not save', { description: describeError(error) }),
  })
}

/** Ticks one checkpoint. The deliverable completes itself when its last step is ticked (a database trigger). */
export function useToggleStep(jobId: string) {
  const invalidate = useInvalidate(jobId)
  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await supabase
        .from('deliverable_steps')
        .update({ done_at: done ? new Date().toISOString() : null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not save', { description: describeError(error) }),
  })
}

export function useAddStep(jobId: string) {
  const invalidate = useInvalidate(jobId)
  return useMutation({
    mutationFn: async (input: { deliverable_id: string; title: string; sort_order: number }) => {
      const { error } = await supabase.from('deliverable_steps').insert(input)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not add that step', { description: describeError(error) }),
  })
}

export function useRemoveStep(jobId: string) {
  const invalidate = useInvalidate(jobId)
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('deliverable_steps').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not remove that step', { description: describeError(error) }),
  })
}

export function useRemoveDeliverable(jobId: string) {
  const invalidate = useInvalidate(jobId)
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('job_deliverables').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not remove that', { description: describeError(error) }),
  })
}

/** Jobs that have happened and are not fully delivered: the editing queues. */
export interface EditingJob {
  id: string
  job_number: number
  kind: Row<'jobs'>['kind']
  title: string | null
  event_date: string
  photos_due_date: string | null
  film_due_date: string | null
  photos_delivered_at: string | null
  film_delivered_at: string | null
  primary_contact: { first_name: string; last_name: string | null } | null
  partner_contact: { first_name: string; last_name: string | null } | null
  deliverables: Array<
    Pick<Deliverable, 'id' | 'kind' | 'done_at' | 'title' | 'internal' | 'sort_order'> & {
      steps: Array<Pick<DeliverableStep, 'id' | 'title' | 'done_at' | 'sort_order'>>
    }
  >
}

const EDITING_SELECT =
  'id, job_number, kind, title, event_date, photos_due_date, film_due_date, photos_delivered_at, film_delivered_at, primary_contact:contacts!jobs_primary_contact_id_fkey(first_name, last_name), partner_contact:contacts!jobs_partner_contact_id_fkey(first_name, last_name), deliverables:job_deliverables(id, kind, done_at, title, internal, sort_order, steps:deliverable_steps(id, title, done_at, sort_order))'

export function useEditingQueue(onDate: string) {
  return useQuery({
    queryKey: [...deliverableKeys.editing, onDate],
    queryFn: async (): Promise<EditingJob[]> => {
      const { data, error } = await supabase
        .from('jobs')
        .select(EDITING_SELECT)
        .is('archived_at', null)
        .lte('event_date', onDate)
        .not('booked_at', 'is', null)
        .or('photos_delivered_at.is.null,film_delivered_at.is.null')
        .order('event_date')
      if (error) throw error
      return data as unknown as EditingJob[]
    },
  })
}

/**
 * The calendar's delivery tasks: every booked wedding, past or coming, that
 * still has something to deliver, plus those from `since` on that are done
 * (so a ticked task stays on the calendar, crossed out). Keyed under the
 * editing queue so a tick anywhere refreshes both.
 */
export function useDeliverySchedule(since: string) {
  return useQuery({
    queryKey: [...deliverableKeys.editing, 'calendar', since],
    queryFn: async (): Promise<EditingJob[]> => {
      const { data, error } = await supabase
        .from('jobs')
        .select(EDITING_SELECT)
        .is('archived_at', null)
        .not('booked_at', 'is', null)
        .or(`event_date.gte.${since},photos_delivered_at.is.null,film_delivered_at.is.null`)
        .order('event_date')
      if (error) throw error
      return data as unknown as EditingJob[]
    },
  })
}

/**
 * Tick a deliverable as delivered from the editing queue: its steps up to and
 * including "Delivered to the couple" are ticked (a step-less one is ticked
 * itself). Unticking clears that step and what comes after it.
 */
export function useSetDelivered() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ item, delivered }: { jobId: string; item: EditingJob['deliverables'][number]; delivered: boolean }) => {
      const at = delivered ? new Date().toISOString() : null
      if (item.steps.length === 0) {
        const { error } = await supabase.from('job_deliverables').update({ done_at: at }).eq('id', item.id)
        if (error) throw error
        return
      }
      const ids = stepsForDelivered(item.steps, delivered)
        .filter((step) => Boolean(step.done_at) !== delivered)
        .map((step) => step.id)
      if (ids.length === 0) return
      const { error } = await supabase.from('deliverable_steps').update({ done_at: at }).in('id', ids)
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: deliverableKeys.editing })
      void queryClient.invalidateQueries({ queryKey: deliverableKeys.forJob(variables.jobId) })
      void queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] })
      void queryClient.invalidateQueries({ queryKey: ['pipeline-board'] })
    },
    onError: (error) => toast.error('Could not save that', { description: describeError(error) }),
  })
}
